import React, {useState, useEffect} from 'react';
import {useTranslation} from 'react-i18next';
import SMCModal from '../../components/core/SMCModal';
import SMCModalBody from '../../components/core/SMCModal/SMCModalBody';
import SMCModalButtonContainer from '../../components/core/SMCModal/SMCModalButtonContainer';
import Api from '../../api/SMPFetchHelper';
import {SessionDataGetSchema} from '../../api/DBSchema/SessionData';
import SMCButton from '../../components/Simple/SMCButton';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import SMCPasswordInput from '../../components/Simple/SMCPasswordInput';
import {hasValue} from '../../utils/utils';

function ChangePasswordModal({
    isOpen,
    onRequestClose,
    onFinish,
    useMinimumLength = true,
    useMaximumLength = true,
    minimumLength,
    maximumLength
}) {
    const { t } = useTranslation();

    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');

    const [passwordMinimumLength, setPasswordMinimumLength] = useState(null);
    const [passwordMaximumLength, setPasswordMaximumLength] = useState(null);
    const [isLoaded, setIsLoaded] = useState(false);
    const [hasErrorLoading, setHasErrorLoading] = useState(false);

    const handleFinish = () => {
        if (password !== confirmPassword) {
            alert(t('com.sofaware.db.CHANGEPASS_NEW_CONF_INVALID'));
        }
        else if (useMinimumLength && passwordMinimumLength && password.length < passwordMinimumLength) {
            alert(t('com.sofaware.db.CHANGEPASS_TOO_SHORT', [passwordMinimumLength]));
        }
        else if (useMaximumLength && passwordMaximumLength && password.length > passwordMaximumLength) {
            alert(t('com.sofaware.db.CHANGEPASS_TOO_LONG', [passwordMaximumLength]));
        }
        else {
            onFinish(password);
        }
    };

    useEffect(() => {
        (async () => {
            const needToFetchMinimumPasswordLength = Boolean(useMinimumLength && !hasValue(minimumLength)); // Need to fetch minimum length if useMinimumLength is set, and minimum length is not set
            const needToFetchMaximumPasswordLength = Boolean(useMaximumLength && !hasValue(maximumLength)); // Need to fetch maximum length if useMaximumLength is set, and maximum length is not set
            if (needToFetchMinimumPasswordLength || needToFetchMaximumPasswordLength) {
                try {
                    const resp = await Api.getSessionData({
                        attributes: [
                            SessionDataGetSchema.minimumPasswordLength,
                            SessionDataGetSchema.maximumPasswordLength
                        ]
                    })
                    needToFetchMinimumPasswordLength && setPasswordMinimumLength(resp.data.minimumPasswordLength);
                    needToFetchMaximumPasswordLength && setPasswordMaximumLength(resp.data.maximumPasswordLength);
                } catch (e) {
                    setHasErrorLoading(true);
                }
            }
            if (useMinimumLength && hasValue(minimumLength)) {
                setPasswordMinimumLength(minimumLength);
            }
            if (useMaximumLength && hasValue(maximumLength)) {
                setPasswordMaximumLength(maximumLength);
            }
            setIsLoaded(true);
        })();
    }, [maximumLength, minimumLength, useMaximumLength, useMinimumLength]);

    // Reset values on each new open
    useEffect(() => {
        if (isOpen) {
            setPassword('');
            setConfirmPassword('');
        }
    }, [isOpen]);
    
    return (
        <SMCModal
            isOpen={isOpen}
            onRequestClose={onRequestClose}
            height={250}
            width={430}
            title={t('SMC_PASSWORD_HEAD')}
            loading={!isLoaded}
            hasError={hasErrorLoading}
        >
            <SMCModalBody lineHeight="22px">
                <SMCPasswordInput
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    label={
                        <>
                        New password
                        {
                            (useMinimumLength || useMaximumLength) && (
                                <>
                                <br />
                                <small>({passwordMinimumLength || 0} {passwordMaximumLength ? '- '+passwordMaximumLength : '+'} characters)</small>
                                </>
                            )
                        }
                        </>
                }
                    allowPasswordPeek={true}
                />
                <SMCPasswordInput
                    value={confirmPassword}
                    onChange={e => setConfirmPassword(e.target.value)}
                    label={t('SMC_PASSWORD_CONFIRM')}
                    allowPasswordPeek={true}
                />
            </SMCModalBody>
            <SMCModalButtonContainer>
                <SMCButton onClick={handleFinish}>
                    <FontAwesomeIcon style={{ marginTop: '3px', marginRight: '5px' }} color="green" icon="check" />
                    {t('SMC_GEN_OK')}
                </SMCButton>
                <SMCButton onClick={onRequestClose}>
                    <FontAwesomeIcon style={{ marginTop: '3px', marginRight: '5px' }} color="grey" icon="times" />
                    {t('SMC_GEN_CANCEL')}
                </SMCButton>
            </SMCModalButtonContainer>
        </SMCModal>
    );
}

export default ChangePasswordModal;